export enum UserRole {
  CONSUMER = 'consumer',
  CREATOR = 'creator',
  ADMIN = 'admin',
  MODERATOR = 'moderator',
}

export enum UserAccountStatus {
  ACTIVE = 'active',
  DEACTIVATED = 'deactivated',
  PENDING_DELETION = 'pending_deletion',
  BANNED = 'banned',
}

export enum CreatorStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
}

/** Paid creator activation lifecycle — persisted on Creator.setupStatus. */
export enum CreatorSetupStatus {
  NOT_CREATOR = 'not_creator',
  INTENT_STARTED = 'intent_started',
  PROFILE_DRAFT = 'profile_draft',
  PREVIEW_READY = 'preview_ready',
  CHECKOUT_STARTED = 'checkout_started',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  SUSPENDED = 'suspended',
}

export enum FeedItemType {
  POST = 'post',
  CREATOR_PROFILE = 'creator_profile',
  PRODUCT = 'product',
}

export enum PostStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  HIDDEN = 'hidden',
  DELETED = 'deleted',
}

export enum PostContext {
  DISCUSSION = 'discussion',
  ASK = 'ask',
  POLL = 'poll',
  SHOW = 'show',
  RESOURCE = 'resource',
}

export enum PostType {
  TEXT = 'text',
  LINK = 'link',
  POLL = 'poll',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  ARTICLE = 'article',
}

/** Comment visibility for long-form articles. */
export enum ArticleCommentPolicy {
  EVERYONE = 'everyone',
  OFF = 'off',
}

export enum PostHideReason {
  NOT_INTERESTING = 'NOT_INTERESTING',
  SLOP = 'SLOP',
  CLICKBAIT = 'CLICKBAIT',
  SPAM = 'SPAM',
  SHILLING = 'SHILLING',
  POLITICAL = 'POLITICAL',
  RUDE = 'RUDE',
  OTHER = 'OTHER',
}

export enum CommentStatus {
  PUBLISHED = 'published',
  HIDDEN = 'hidden',
  DELETED = 'deleted',
}

export enum ReportTargetType {
  POST = 'post',
  USER = 'user',
  COMMENT = 'comment',
  MESSAGE = 'message',
  ARTICLE = 'article',
  COMMUNITY_MEMBER = 'community_member',
  COURSE = 'course',
  COURSE_LESSON_COMMENT = 'course_lesson_comment',
  MEMBERSHIP = 'membership',
  CREATOR_SITE = 'creator_site',
  SERVICE_OFFER = 'service_offer',
}

export enum ReportReason {
  SPAM = 'spam',
  HARASSMENT = 'harassment',
  MISINFORMATION = 'misinformation',
  IMPERSONATION = 'impersonation',
  ADULT_CONTENT = 'adult_content',
  OTHER = 'other',
}

export enum ReportStatus {
  OPEN = 'open',
  REVIEWING = 'reviewing',
  RESOLVED = 'resolved',
  DISMISSED = 'dismissed',
}

export enum NotificationType {
  NEW_FOLLOWER = 'new_follower',
  POST_LIKED = 'post_liked',
  POST_COMMENTED = 'post_commented',
  CREATOR_POST = 'creator_post',
  MENTION = 'mention',
  DIRECT_MESSAGE = 'direct_message',
  POST_REPOSTED = 'post_reposted',
  POST_SAVED = 'post_saved',
  CREATOR_PROFILE_COMPLETED = 'creator_profile_completed',
  MODERATION_ACTION = 'moderation_action',
  SUBSCRIPTION_UPDATED = 'subscription_updated',
  COMMUNITY_JOIN_WELCOME = 'community_join_welcome',
  COMMUNITY_REPLY = 'community_reply',
  COMMUNITY_MENTION = 'community_mention',
  COMMUNITY_PIN = 'community_pin',
  COMMUNITY_MOD_ACTION = 'community_mod_action',
  COMMUNITY_BILLING = 'community_billing',
  COMMUNITY_APPLICATION_REVIEWED = 'community_application_reviewed',
  COMMUNITY_STREAK_BREAK = 'community_streak_break',
  COMMUNITY_CHALLENGE_COMPLETE = 'community_challenge_complete',
  COURSE_DRIP_UNLOCK = 'course_drip_unlock',
  COURSE_CERTIFICATE_READY = 'course_certificate_ready',
  COURSE_NEW_LESSON = 'course_new_lesson',
  COURSE_QA_REPLY = 'course_qa_reply',
  COURSE_ENROLLED = 'course_enrolled',
  COURSE_PURCHASE_RECEIPT = 'course_purchase_receipt',
  EVENT_REMINDER = 'event_reminder',
}

export enum NotificationDeliveryChannel {
  IN_APP = 'in_app',
  EMAIL = 'email',
  PUSH = 'push',
}

export enum NotificationDeliveryStatus {
  PENDING = 'pending',
  SCHEDULED = 'scheduled',
  SENT = 'sent',
  SKIPPED = 'skipped',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export enum MediaAssetStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  REJECTED = 'rejected',
}

export enum ProductStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
}

export enum CommunityStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
}

export enum CommunityMembershipRole {
  OWNER = 'owner',
  MODERATOR = 'moderator',
  MEMBER = 'member',
}

export enum CommunityJoinMode {
  OPEN = 'open',
  INVITE_ONLY = 'invite_only',
}

export enum CommunityListingVisibility {
  LISTED = 'listed',
  UNLISTED = 'unlisted',
  SECRET = 'secret',
}

export enum CommunityMemberMessagingMode {
  OWNER_MOD_ONLY = 'owner_mod_only',
  MEMBERS_ALLOWED = 'members_allowed',
}

export enum CommunityDigestSchedule {
  INHERIT = 'inherit',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  OFF = 'off',
}

export enum CommunityMembershipStatus {
  ACTIVE = 'active',
  BANNED = 'banned',
  LEFT = 'left',
}

export enum CommunityAccessType {
  FREE = 'free',
  PAID = 'paid',
}

export enum CommunityBillingInterval {
  MONTH = 'month',
  YEAR = 'year',
}

export enum ProductType {
  DIGITAL = 'digital',
}

export enum PurchaseStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  PAST_DUE = 'past_due',
}

export enum SubscriptionTier {
  FREE = 'free',
  PRO = 'pro',
  CREATOR_PLAN = 'creator_plan',
}

export enum PermissionKey {
  CAN_CREATE_POST = 'can_create_post',
  CAN_CREATE_MEMBER_POST = 'can_create_member_post',
  CAN_CREATE_ARTICLE = 'can_create_article',
  CAN_DELETE_POST = 'can_delete_post',
  CAN_LIKE = 'can_like',
  CAN_COMMENT = 'can_comment',
  CAN_BOOKMARK = 'can_bookmark',
  CAN_REPORT = 'can_report',
  CAN_FOLLOW_USER = 'can_follow_user',
  CAN_FOLLOW_CATEGORY = 'can_follow_category',
  CAN_BLOCK = 'can_block',
  CAN_MUTE = 'can_mute',
  CAN_MESSAGE = 'can_message',
  CAN_BAN_USER = 'can_ban_user',
  CAN_VIEW_ANALYTICS = 'can_view_analytics',
  CAN_MANAGE_OWN_SUBSCRIPTION = 'can_manage_own_subscription',
  CAN_MANAGE_SUBSCRIPTIONS = 'can_manage_subscriptions',
  CAN_MODERATE_CONTENT = 'can_moderate_content',
  CAN_ACCESS_ADMIN_TOOLS = 'can_access_admin_tools',
  CAN_MANAGE_MAIL_TEMPLATES = 'can_manage_mail_templates',
  CAN_MANAGE_NOTIFICATIONS = 'can_manage_notifications',
  CAN_MANAGE_COMMUNITIES = 'can_manage_communities',
  CAN_MANAGE_EVENTS = 'can_manage_events',
  CAN_MANAGE_AUDIENCE = 'can_manage_audience',
  CAN_VIEW_CREATOR_ANALYTICS = 'can_view_creator_analytics',
  CAN_MANAGE_MARKETING = 'can_manage_marketing',
  /** Confirm-to-send + campaign/edition fan-out (Marketing S01+). */
  CAN_SEND_MARKETING = 'can_send_marketing',
  /** Sequence/automation publish and enroll mutations (Marketing S01+). */
  CAN_MANAGE_MARKETING_AUTOMATIONS = 'can_manage_marketing_automations',
  CAN_MANAGE_GROWTH = 'can_manage_growth',
  CAN_MANAGE_DOMAINS = 'can_manage_domains',
  CAN_MANAGE_SERVICES = 'can_manage_services',
  CAN_MANAGE_FEATURE_FLAGS = 'can_manage_feature_flags',
}

export enum DomainEventType {
  USER_CREATED = 'user_created',
  POST_CREATED = 'post_created',
  POST_DELETED = 'post_deleted',
  POST_LIKED = 'post_liked',
  COMMENT_CREATED = 'comment_created',
  USER_FOLLOWED = 'user_followed',
  USER_FOLLOWED_MEMBER = 'user_followed_member',
  REPORT_CREATED = 'report_created',
  NOTIFICATION_CREATED = 'notification_created',
  PRODUCT_CREATED = 'product_created',
  PURCHASE_COMPLETED = 'purchase_completed',
  ADMIN_ACTION = 'admin_action',
  CREATOR_SETUP_STARTED = 'creator_setup_started',
  CREATOR_CHECKOUT_STARTED = 'creator_checkout_started',
  CREATOR_ACTIVATED = 'creator_activated',
  CREATOR_INTENT_STARTED = 'creator_intent_started',
  CREATOR_SIGNUP_STARTED = 'creator_signup_started',
  CREATOR_PROFILE_DRAFT_SAVED = 'creator_profile_draft_saved',
  CREATOR_PREVIEW_VIEWED = 'creator_preview_viewed',
  CREATOR_PAYMENT_COMPLETED = 'creator_payment_completed',
  CREATOR_PROFILE_VIEWED = 'creator_profile_viewed',
  CREATOR_FOLLOWED = 'creator_followed',
  CREATOR_INTRO_POST_PUBLISHED = 'creator_intro_post_published',
  STUDIO_OPENED = 'studio_opened',
  COMMUNITY_CREATED = 'community_created',
  COMMUNITY_PUBLISHED = 'community_published',
  COMMUNITY_UNPUBLISHED = 'community_unpublished',
  COMMUNITY_ARCHIVED = 'community_archived',
  COMMUNITY_MEMBER_JOINED = 'community_member_joined',
  COMMUNITY_POST_CREATED = 'community_post_created',
  COMMUNITY_POST_UPDATED = 'community_post_updated',
  COMMUNITY_POST_DELETED = 'community_post_deleted',
  COMMUNITY_POST_PINNED = 'community_post_pinned',
  COMMUNITY_MEMBER_BANNED = 'community_member_banned',
  COMMUNITY_MEMBER_REMOVED = 'community_member_removed',
  COMMUNITY_MODERATOR_ASSIGNED = 'community_moderator_assigned',
  COMMUNITY_MODERATOR_REMOVED = 'community_moderator_removed',
  COMMUNITY_OWNERSHIP_TRANSFER_INITIATED = 'community_ownership_transfer_initiated',
  COMMUNITY_OWNERSHIP_TRANSFER_ACCEPTED = 'community_ownership_transfer_accepted',
  COMMUNITY_OWNERSHIP_TRANSFER_CANCELLED = 'community_ownership_transfer_cancelled',
  COURSE_DRIP_UNLOCKED = 'course_drip_unlocked',
  COURSE_ENROLLED = 'course_enrolled',
  COURSE_COMPLETED = 'course_completed',
  COURSE_CERTIFICATE_ISSUED = 'course_certificate_issued',
}
